import { Eye, RotateCcw, Save } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import api, { formatApiError } from "../lib/api";

const copySections = {
  home: { label: "Forside", fields: [
    ["hero.eyebrow", "Hero overtekst", "short"], ["hero.title", "Hero-overskrift", "heading"], ["hero.intro", "Hero-ingress", "long"], ["hero.primary_label", "Primærknapp", "short"], ["hero.secondary_label", "Sekundærknapp", "short"],
    ["copy.cars_eyebrow", "Bilseksjon overtekst", "short"], ["copy.cars_title", "Bilseksjon overskrift", "heading"], ["copy.cars_intro", "Bilseksjon brødtekst", "long"], ["copy.all_cars_label", "FINN-knapp", "short"],
    ["copy.sell_eyebrow", "Salgseksjon overtekst", "short"], ["copy.sell_title", "Salgseksjon overskrift", "heading"], ["copy.sell_body", "Salgseksjon brødtekst", "long"],
    ["copy.form_name_label", "Skjema: navn", "short"], ["copy.form_phone_label", "Skjema: telefon", "short"], ["copy.form_email_label", "Skjema: e-post", "short"], ["copy.form_registration_label", "Skjema: registreringsnummer", "short"], ["copy.form_message_label", "Skjema: melding", "short"], ["copy.form_message_placeholder", "Skjema: hjelpetekst i melding", "long"], ["copy.form_submit_label", "Skjema: sendeknapp", "short"], ["copy.form_sending_label", "Skjema: sender-status", "short"], ["copy.form_success_message", "Skjema: bekreftelse", "long"], ["copy.form_error_message", "Skjema: feilmelding", "long"],
  ] },
  about: { label: "Om oss", fields: [
    ["about_eyebrow", "Overtekst", "short"], ["about_title", "Overskrift", "heading"], ["about_body", "Brødtekst", "long"], ["copy.about_buy_label", "Lenke: kjøp bil", "short"], ["copy.about_sell_label", "Lenke: selg bil", "short"], ["copy.about_commission_label", "Lenke: kommisjonssalg", "short"],
  ] },
  contact: { label: "Kontakt", fields: [
    ["copy.contact_eyebrow", "Overtekst", "short"], ["copy.contact_title", "Overskrift", "heading"], ["copy.contact_visit_label", "Etikett: besøk", "short"], ["copy.contact_phone_label", "Etikett: telefon", "short"], ["copy.contact_email_label", "Etikett: e-post", "short"], ["copy.contact_hours_label", "Etikett: åpningstider", "short"], ["contact.address", "Adresse", "short"], ["contact.postal_city", "Poststed", "short"], ["contact.phone", "Telefonnummer", "short"], ["contact.email", "E-post", "short"], ["contact.opening_hours", "Åpningstider – én linje per tidspunkt", "list"],
  ] },
  footer: { label: "Navigasjon og footer", fields: [
    ["navigation.0", "Meny: biler", "short"], ["navigation.1", "Meny: selg bilen", "short"], ["navigation.2", "Meny: om oss", "short"], ["navigation.3", "Meny: kontakt", "short"], ["footer_note", "Footer-slogan", "long"], ["copy.footer_admin_label", "Lenke: administrasjon", "short"], ["copy.footer_instagram_label", "Lenke: Instagram", "short"], ["copy.footer_copyright_text", "Opphavsrettsnavn", "short"], ["copy.vehicle_details_label", "Bilkort: detaljknapp", "short"], ["copy.vehicle_finn_label", "Bilkort: FINN-knapp", "short"],
  ] },
};

const fieldLimits = {
  "hero.eyebrow": 40,
  "hero.title": 120,
  "hero.intro": 300,
  "hero.primary_label": 60,
  "hero.secondary_label": 60,
  "about_eyebrow": 40,
  "about_title": 120,
  "about_body": 300,
  "contact.address": 100,
  "contact.postal_city": 60,
  "contact.phone": 30,
  "contact.email": 120,
  "contact.opening_hours": 220,
  "navigation.0": 60,
  "navigation.1": 60,
  "navigation.2": 60,
  "navigation.3": 60,
  "footer_note": 160,
  "copy.cars_eyebrow": 40,
  "copy.cars_title": 100,
  "copy.cars_intro": 260,
  "copy.all_cars_label": 60,
  "copy.sell_eyebrow": 40,
  "copy.sell_title": 100,
  "copy.sell_body": 260,
  "copy.form_name_label": 30,
  "copy.form_phone_label": 30,
  "copy.form_email_label": 30,
  "copy.form_registration_label": 40,
  "copy.form_message_label": 30,
  "copy.form_message_placeholder": 240,
  "copy.form_submit_label": 60,
  "copy.form_sending_label": 40,
  "copy.form_success_message": 160,
  "copy.form_error_message": 120,
  "copy.about_buy_label": 50,
  "copy.about_sell_label": 50,
  "copy.about_commission_label": 50,
  "copy.contact_eyebrow": 40,
  "copy.contact_title": 100,
  "copy.contact_visit_label": 40,
  "copy.contact_phone_label": 40,
  "copy.contact_email_label": 40,
  "copy.contact_hours_label": 40,
  "copy.footer_admin_label": 40,
  "copy.footer_instagram_label": 40,
  "copy.footer_copyright_text": 60,
  "copy.vehicle_details_label": 40,
  "copy.vehicle_finn_label": 40,
};

function readPath(source, path) {
  return path.split(".").reduce((value, key) => value?.[key], source);
}

function writePath(source, path, value) {
  const keys = path.split(".");
  const next = structuredClone(source);
  let target = next;
  keys.slice(0, -1).forEach((key) => { target = target[key]; });
  target[keys.at(-1)] = value;
  return next;
}

function InputField({ field, form, onChange, onReset, defaults }) {
  const [path, label, type] = field;
  const value = readPath(form, path) || "";
  const maxLength = fieldLimits[path];
  const multiline = ["heading", "long", "list"].includes(type);
  const displayedValue = type === "list" ? value.join("\n") : value;
  const update = (event) => onChange(path, type === "list" ? event.target.value.split("\n").map((item) => item.trim()).filter(Boolean) : event.target.value);

  return (
    <label className="copy-field" data-testid={`copy-field-${path.replaceAll(".", "-")}`}>
      <span>{label}</span>
      {multiline ? <textarea data-testid={`copy-input-${path.replaceAll(".", "-")}`} maxLength={maxLength} onChange={update} rows={type === "long" ? 4 : 2} value={displayedValue} /> : <input data-testid={`copy-input-${path.replaceAll(".", "-")}`} maxLength={maxLength} onChange={update} value={displayedValue} />}
      <div className="copy-field__meta"><small>{typeof displayedValue === "string" ? displayedValue.length : 0}/{maxLength}</small><button data-testid={`copy-reset-${path.replaceAll(".", "-")}`} onClick={() => onReset(path, readPath(defaults, path))} type="button"><RotateCcw size={13} />Tilbakestill</button></div>
    </label>
  );
}

export default function AdminTextEditor({ saving, settings, onSave }) {
  const [form, setForm] = useState(settings);
  const [defaults, setDefaults] = useState(null);
  const [section, setSection] = useState("home");
  const [error, setError] = useState("");

  useEffect(() => setForm(settings), [settings]);
  useEffect(() => { api.get("/admin/defaults").then((response) => setDefaults(response.data)).catch((requestError) => setError(formatApiError(requestError))); }, []);

  const fields = useMemo(() => copySections[section].fields, [section]);
  const update = (path, value) => setForm((current) => writePath(current, path, value));
  const preview = () => {
    window.localStorage.setItem("urban-motors-content-preview", JSON.stringify(form));
    window.open("/?preview=1", "_blank", "noopener,noreferrer");
  };

  return (
    <section className="copy-editor" data-testid="admin-text-editor">
      <div className="admin-section-title"><div><p className="section-eyebrow">Låst design · redigerbart innhold</p><h1>Tekster</h1></div><div className="copy-editor__actions"><button className="button admin-preview-button" data-testid="preview-copy-button" onClick={preview}><Eye size={16} />Forhåndsvis</button><button className="button button--light" data-testid="save-copy-button" disabled={saving} onClick={() => onSave(form)}><Save size={16} />{saving ? "Lagrer..." : "Publiser tekster"}</button></div></div>
      <div className="copy-section-tabs" data-testid="copy-section-tabs">{Object.entries(copySections).map(([key, item]) => <button className={section === key ? "active" : ""} data-testid={`copy-section-${key}`} key={key} onClick={() => setSection(key)}>{item.label}</button>)}</div>
      {error && <p className="form-status form-status--error" data-testid="copy-editor-error">{error}</p>}
      {defaults && <div className="copy-fields">{fields.map((field) => <InputField defaults={defaults} field={field} form={form} key={field[0]} onChange={update} onReset={update} />)}</div>}
    </section>
  );
}